import React, { useEffect, useState } from 'react';
import api from './api';
import { userDisplayName, timeAgo, taskCode, isoToDisplayDate, displayDateToIso, PURCHASE_STATUS_LABELS, TASK_PRIORITIES, PRIORITY_LABELS, BLOCAGE_ORIGINES, BLOCAGE_ORIGINE_LABELS, libelleBlocage, joursDeBlocage, niveauEscalade, ESCALADE_SERVICE_JOURS, ESCALADE_DIRECTION_JOURS, useFermerAvecEchap, userInitials } from './helpers';
import AttachmentsPanel from './AttachmentsPanel';

const STATUS_OPTIONS = ['Todo', 'In_Progress', 'In_Review', 'Done', 'Blocked'];
const STATUS_LABELS = { Todo: 'À faire', In_Progress: 'En cours', In_Review: 'Revue', Done: 'Terminé', Blocked: 'Bloqué' };

function TaskModal({ task, projectId, users, currentUser, initialStatus, onClose, onSaved, onDeleted }) {
  useFermerAvecEchap(onClose);
  const isEdit = !!task;
  const [form, setForm] = useState({
    name: task?.name || '',
    description: task?.description || '',
    status: task?.status || initialStatus || 'Todo',
    priority: task?.priority || 'Medium',
    assigned_to: task?.assigned_to || (users[0]?.id ?? ''),
    delegated_to: task?.delegated_to || '',
    start_date: isoToDisplayDate(task?.start_date),
    due_date: isoToDisplayDate(task?.due_date),
    blocking_issue: task?.blocking_issue || '',
    blocage_motif: task?.blocage_motif || '',
    blocage_origine: task?.blocage_origine || 'personne',
    blocage_qui: task?.blocage_qui || '',
    purchase_request: task?.purchase_request || '',
    purchase_status: task?.purchase_status || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [subtasks, setSubtasks] = useState([]);
  const [newSubtask, setNewSubtask] = useState('');
  const [taskProgress, setTaskProgress] = useState(task?.progress_percent || 0);

  useEffect(() => {
    if (isEdit) {
      api.get('/comments', { params: { taskId: task.id } }).then((res) => setComments(res.data)).catch(() => {});
      api.get('/subtasks', { params: { taskId: task.id } }).then((res) => setSubtasks(res.data)).catch(() => {});
    }
  }, [isEdit, task]);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const startIso = displayDateToIso(form.start_date);
    if (form.start_date && startIso === null) {
      setError('Date de début invalide — utilisez le format jj/mm/aaaa.');
      return;
    }
    const dueIso = displayDateToIso(form.due_date);
    if (form.due_date && dueIso === null) {
      setError('Date d’échéance invalide — utilisez le format jj/mm/aaaa.');
      return;
    }
    if (startIso && dueIso && startIso > dueIso) {
      setError('La date de début doit être avant (ou égale à) la date d’échéance.');
      return;
    }

    if (form.status === 'Blocked') {
      if (!form.blocage_motif.trim()) {
        setError('Dites ce que vous attendez pour débloquer la tâche.');
        return;
      }
      if (!form.blocage_qui.trim()) {
        setError('Indiquez de qui — ou de quel service — vous l’attendez.');
        return;
      }
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        start_date: startIso || null,
        due_date: dueIso || null,
        project_id: projectId,
        assigned_to: parseInt(form.assigned_to),
        delegated_to: form.delegated_to ? parseInt(form.delegated_to) : null,
      };
      let res;
      if (isEdit) {
        res = await api.put(`/tasks/${task.id}`, payload);
      } else {
        res = await api.post('/tasks', payload);
      }
      onSaved(res.data);
    } catch (err) {
      setError("Impossible d'enregistrer la tâche.");
    } finally {
      setSaving(false);
    }
  };

  // Statut de blocage tel qu'il est aujourd'hui enregistré côté serveur.
  const blocageEnCours = task && task.status === 'Blocked' && task.blocage_depuis ? task : null;
  const joursBloques = blocageEnCours ? joursDeBlocage(blocageEnCours) : 0;
  const escalade = niveauEscalade(joursBloques);

  const handleDelete = async () => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      onDeleted(task.id);
    } catch (err) {
      setError('Impossible de supprimer la tâche.');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      const res = await api.post('/comments', {
        task_id: task.id,
        project_id: projectId,
        user_id: currentUser?.id,
        comment_text: newComment.trim(),
      });
      setComments([...comments, res.data]);
      setNewComment('');
    } catch (err) {
      setError("Impossible d'ajouter le commentaire.");
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/comments/${commentId}`);
      setComments(comments.filter((c) => c.id !== commentId));
    } catch (err) {
      setError('Impossible de supprimer le commentaire.');
    }
  };

  const recomputeProgress = (list) => {
    if (list.length === 0) return;
    const done = list.filter((s) => s.is_done).length;
    setTaskProgress(Math.round((done / list.length) * 100));
  };

  const handleAddSubtask = async (e) => {
    e.preventDefault();
    if (!newSubtask.trim()) return;
    try {
      const res = await api.post('/subtasks', {
        task_id: task.id,
        title: newSubtask.trim(),
        is_done: false,
      });
      const updated = [...subtasks, res.data];
      setSubtasks(updated);
      recomputeProgress(updated);
      setNewSubtask('');
    } catch (err) {
      setError("Impossible d'ajouter l'élément de checklist.");
    }
  };

  const handleToggleSubtask = async (subtask) => {
    try {
      const res = await api.put(`/subtasks/${subtask.id}`, { is_done: !subtask.is_done });
      const updated = subtasks.map((s) => (s.id === subtask.id ? res.data : s));
      setSubtasks(updated);
      recomputeProgress(updated);
    } catch (err) {
      setError('Impossible de mettre à jour la checklist.');
    }
  };

  const handleDeleteSubtask = async (subtaskId) => {
    try {
      await api.delete(`/subtasks/${subtaskId}`);
      const updated = subtasks.filter((s) => s.id !== subtaskId);
      setSubtasks(updated);
      if (updated.length > 0) recomputeProgress(updated);
    } catch (err) {
      setError("Impossible de supprimer l'élément de checklist.");
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>
            {isEdit && <span style={{ color: 'var(--text-muted)', fontWeight: 500, marginRight: 8 }}>{taskCode(task.id)}</span>}
            {isEdit ? task.name : 'Nouvelle tâche'}
          </h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {error && <div className="error-message" style={{ marginBottom: 14 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Titre</label>
            <input type="text" value={form.name} onChange={handleChange('name')} required />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea value={form.description} onChange={handleChange('description')} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Statut</label>
              <select value={form.status} onChange={handleChange('status')}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Priorité</label>
              <select value={form.priority} onChange={handleChange('priority')}>
                {TASK_PRIORITIES.map((p) => (
                  <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Responsable</label>
              <select value={form.assigned_to} onChange={handleChange('assigned_to')}>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{userDisplayName(u)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Déléguée à <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(facultatif)</span></label>
              <select value={form.delegated_to} onChange={handleChange('delegated_to')}>
                <option value="">— Personne —</option>
                {users
                  .filter((u) => String(u.id) !== String(form.assigned_to))
                  .map((u) => (
                    <option key={u.id} value={u.id}>{userDisplayName(u)}</option>
                  ))}
              </select>
            </div>
          </div>

          {form.delegated_to && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: -6, marginBottom: 14 }}>
              Le responsable reste <strong>{userDisplayName(users.find((u) => String(u.id) === String(form.assigned_to)))}</strong> ;
              la tâche est réalisée par <strong>{userDisplayName(users.find((u) => String(u.id) === String(form.delegated_to)))}</strong>.
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Date de début</label>
              <input type="text" placeholder="jj/mm/aaaa" value={form.start_date} onChange={handleChange('start_date')} />
            </div>
            <div className="form-group">
              <label>Échéance</label>
              <input type="text" placeholder="jj/mm/aaaa" value={form.due_date} onChange={handleChange('due_date')} />
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>
              Demande d'achat <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(si la tâche implique un approvisionnement)</span>
            </div>
            {/* Seul le numéro se saisit ici ; le statut de l'achat se suit
                depuis la page Achats, où l'on a la vue d'ensemble. */}
            <div className="form-group">
              <label>N° de requête</label>
              <input
                type="text"
                value={form.purchase_request}
                onChange={handleChange('purchase_request')}
                placeholder="ZMSM-R-20540"
              />
              {form.purchase_request && (
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>
                  Statut actuel : {PURCHASE_STATUS_LABELS[form.purchase_status] || 'À lancer'} — modifiable depuis la page Achats.
                </div>
              )}
            </div>
          </div>

          {form.status === 'Blocked' && (
            <div className="blocage-bloc">
              <div className="blocage-entete">
                Blocage
                {blocageEnCours && (
                  <span className={`blocage-compteur${escalade ? ` blocage-compteur-${escalade}` : ''}`}>
                    {libelleBlocage(blocageEnCours)}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label>On attend quoi ?</label>
                <textarea
                  rows={2}
                  value={form.blocage_motif}
                  onChange={handleChange('blocage_motif')}
                  placeholder="Ex. : la validation du devis, la pièce de rechange, l'accord HSE..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>De qui ?</label>
                  <select value={form.blocage_origine} onChange={handleChange('blocage_origine')}>
                    {BLOCAGE_ORIGINES.map((o) => (
                      <option key={o} value={o}>{BLOCAGE_ORIGINE_LABELS[o]}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>
                    {form.blocage_origine === 'service'
                      ? 'Quel service ?'
                      : form.blocage_origine === 'externe'
                      ? 'Quel intervenant ?'
                      : 'Quelle personne ?'}
                  </label>
                  <input
                    type="text"
                    value={form.blocage_qui}
                    onChange={handleChange('blocage_qui')}
                    placeholder={
                      form.blocage_origine === 'service'
                        ? 'Maintenance'
                        : form.blocage_origine === 'externe'
                        ? 'Nom du fournisseur'
                        : 'Nom de la personne'
                    }
                  />
                </div>
              </div>

              <p className="blocage-note">
                {blocageEnCours
                  ? `Bloquée depuis le ${isoToDisplayDate(blocageEnCours.blocage_depuis)}. `
                  : "Le décompte démarre aujourd'hui. "}
                Sans levée du blocage, l’affaire remonte automatiquement au chef de
                projet et au chef de service au bout de {ESCALADE_SERVICE_JOURS} jours,
                puis à la direction au bout de {ESCALADE_DIRECTION_JOURS} jours.
              </p>
            </div>
          )}

          <div className="modal-footer">
            {isEdit && (
              <button type="button" className="btn btn-danger" onClick={handleDelete}>
                Supprimer
              </button>
            )}
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>

        {isEdit && (
          <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Checklist</span>
              {subtasks.length > 0 && (
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontWeight: 500 }}>
                  {subtasks.filter((s) => s.is_done).length}/{subtasks.length} · {taskProgress}%
                </span>
              )}
            </h3>
            {subtasks.length > 0 && (
              <div className="progress-bar" style={{ marginBottom: 12 }}>
                <div className="progress-fill" style={{ width: `${taskProgress}%` }} />
              </div>
            )}
            <div className="subtask-list">
              {subtasks.length === 0 && (
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 10 }}>
                  Aucun élément de checklist. Le taux d'avancement de la tâche sera calculé automatiquement dès qu'un élément sera ajouté.
                </div>
              )}
              {subtasks.map((s) => (
                <div key={s.id} className="subtask-item" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
                  <input type="checkbox" checked={!!s.is_done} onChange={() => handleToggleSubtask(s)} />
                  <span style={{ flex: 1, fontSize: 13.5, textDecoration: s.is_done ? 'line-through' : 'none', color: s.is_done ? 'var(--text-muted)' : 'inherit' }}>
                    {s.title}
                  </span>
                  <span className="comment-delete" onClick={() => handleDeleteSubtask(s.id)}>Supprimer</span>
                </div>
              ))}
            </div>
            <form className="comment-form" onSubmit={handleAddSubtask} style={{ marginTop: 8 }}>
              <input
                type="text"
                placeholder="Ajouter un élément de checklist..."
                value={newSubtask}
                onChange={(e) => setNewSubtask(e.target.value)}
              />
              <button type="submit" className="btn btn-primary btn-sm">Ajouter</button>
            </form>
          </div>
        )}

        {isEdit && (
          <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            <h3>Commentaires</h3>
            <div className="comments-list">
              {comments.length === 0 && (
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun commentaire.</div>
              )}
              {comments.map((c) => {
                const author = users.find((u) => u.id === c.user_id);
                return (
                  <div key={c.id} className="comment-item">
                    <div className="avatar">{userInitials(author)}</div>
                    <div className="comment-bubble">
                      <span className="comment-author">{userDisplayName(author)}</span>
                      <span className="comment-time">{timeAgo(c.created_at)}</span>
                      <div className="comment-text">{c.comment_text}</div>
                      {currentUser?.id === c.user_id && (
                        <span className="comment-delete" onClick={() => handleDeleteComment(c.id)}>Supprimer</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <form className="comment-form" onSubmit={handleAddComment}>
              <input
                type="text"
                placeholder="Ajouter un commentaire… tapez @ pour mentionner"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                list="liste-mentions"
              />
              <datalist id="liste-mentions">
                {users.map((u) => (
                  <option key={u.id} value={`@${u.username} `} />
                ))}
              </datalist>
              <button type="submit" className="btn btn-primary btn-sm">Publier</button>
            </form>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6 }}>
              Écrivez <code>@identifiant</code> pour notifier quelqu'un directement.
            </div>
          </div>
        )}

        {isEdit && (
          <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            <h3>Documents</h3>
            <AttachmentsPanel
              projectId={projectId}
              taskId={task.id}
              users={users}
              currentUser={currentUser}
              emptyLabel="Aucun document joint à cette tâche."
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default TaskModal;
